import { useId } from 'react';
import { BlockStack } from '../block-stack/block-stack';
import { Typography } from '../typography/typography';
import { Icon } from '../icon/icon';
import { selectShell, selectInput, selectChevron, selectError } from './select.css';

export interface SelectOption {
    value: string;
    label: string;
    /** Disable this single option (e.g. a site that isn't currently crawlable). */
    disabled?: boolean;
}

export interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: readonly SelectOption[];
    /** Visible label, associated with the control for assistive tech. */
    label?: string;
    /**
     * Accessible name when there is no visible `label`. Required if `label` is
     * omitted — the control must never be unlabelled.
     */
    accessibilityLabel?: string;
    /**
     * Shown as a disabled leading option while `value` is empty — a prompt like
     * "Choose a site…". Selecting it is never a valid value.
     */
    placeholder?: string;
    disabled?: boolean;
    /** Error message; shown below and wired via `aria-describedby` + `aria-invalid`. */
    error?: string;
    testID?: string;
}

/**
 * Single-select dropdown — Untitled UI style: the same rounded shell, hairline
 * border and primary focus ring as `TextField`, with a trailing chevron. Wraps a
 * native `<select>` (chrome stripped) so keyboard, screen-reader and mobile
 * behaviour come for free; never pins a fixed height, and 16px text so it scales
 * with the OS type setting without zoom.
 */
export function Select({
    value,
    onChange,
    options,
    label,
    accessibilityLabel,
    placeholder,
    disabled,
    error,
    testID,
}: SelectProps) {
    const selectId = useId();
    const errorId = `${selectId}-error`;
    const showingPlaceholder = value === '' && placeholder !== undefined;

    const control = (
        <div className={selectShell} data-invalid={error ? true : undefined}>
            <select
                id={label ? selectId : undefined}
                className={selectInput}
                value={value}
                disabled={disabled}
                data-placeholder={showingPlaceholder ? true : undefined}
                aria-label={label ? undefined : accessibilityLabel}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                data-testid={testID}
                onChange={(event) => onChange(event.target.value)}
            >
                {placeholder !== undefined ? (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                ) : null}
                {options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                    </option>
                ))}
            </select>
            <span className={selectChevron} aria-hidden>
                <Icon name="chevron-down" />
            </span>
        </div>
    );

    // Bare control only when there's nothing to wrap it with.
    if (!label && !error) return control;

    return (
        <BlockStack gap="150">
            {label ? (
                <label htmlFor={selectId}>
                    <Typography as="span" variant="body-sm" fontWeight="medium" tone="text-secondary">
                        {label}
                    </Typography>
                </label>
            ) : null}
            {control}
            {error ? (
                <span id={errorId} className={selectError} role="alert">
                    {error}
                </span>
            ) : null}
        </BlockStack>
    );
}
